import { cn } from "@/lib/utils";

import { ImageThumbnail } from "../image-thumbnail/ImageThumbnail";
import { ImageStripActions } from "./ImageStripActions";
import { ImageStripBusyOverlay } from "./ImageStripBusyOverlay";
import { ImageStripImage } from "./ImageStripImage";
import type { ImageStripItem } from "./ImageStripItem";
import { imageStripFrameClass } from "./imageStripLayout";

export function ImageStripTile({
    image,
    isCover,
    isDragging = false,
    isDropTarget = false,
    sortable = false,
    showActions,
    busy = false,
    tileRef,
    onPlaceholderClick,
    onMoveToFirst,
    onRemove,
}: {
    image: ImageStripItem;
    isCover: boolean;
    isDragging?: boolean;
    isDropTarget?: boolean;
    sortable?: boolean;
    showActions: boolean;
    busy?: boolean;
    tileRef?: (element: Element | null) => void;
    onPlaceholderClick?: () => void;
    onMoveToFirst?: () => void;
    onRemove: () => void;
}) {
    const isCoverReplaceTarget = isCover && isDropTarget;
    const thumbnail = image.src ? (
        <ImageStripImage
            key={image.src}
            src={image.src}
            alt={image.alt ?? ""}
            isCover={isCover}
            width={image.width}
            height={image.height}
        />
    ) : (
        <ImageThumbnail
            alt={image.alt ?? ""}
            className={imageStripFrameClass(isCover, false)}
        />
    );

    const tileClassName = cn(
        "group relative shrink-0 snap-start",
        sortable && "cursor-grab",
        isDragging && "cursor-grabbing opacity-50",
        isDropTarget && !isCover && "ring-2 ring-zinc-900 ring-offset-2",
    );

    if (onPlaceholderClick) {
        return (
            <button
                type="button"
                className={cn(
                    tileClassName,
                    "cursor-pointer border-0 bg-transparent p-0",
                )}
                onClick={onPlaceholderClick}
            >
                {thumbnail}
            </button>
        );
    }

    const media =
        image.href && !showActions ? (
            <a
                href={image.href}
                target="_blank"
                rel="noreferrer"
                className="inline-block"
            >
                {thumbnail}
            </a>
        ) : (
            thumbnail
        );

    return (
        <div ref={tileRef} className={tileClassName}>
            {media}
            {isCoverReplaceTarget ? (
                <div
                    className="pointer-events-none absolute inset-0 rounded-md bg-white/65"
                    aria-hidden
                />
            ) : null}
            {showActions ? (
                <ImageStripActions onMoveToFirst={onMoveToFirst} onRemove={onRemove} />
            ) : null}
            {busy ? <ImageStripBusyOverlay /> : null}
        </div>
    );
}
